import React, { useState, useEffect } from 'react';
import { AlertCircle, FolderSearch, X } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { directorApiClient } from '../services/directorApi';
import { useOrganization } from '../hooks/useOrganization';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { Spinner } from '@/components/ui/spinner';
import { DEFAULT_SCRIPT_MODE, normalizeScriptMode } from '@/lib/videoConfig';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

// Import New Phases
import CastingPhase from '../components/director/CastingPhase';
import BriefPhase from '../components/director/BriefPhase';
import StudioPhase from '../components/director/StudioPhase';
import PremierePhase from '../components/director/PremierePhase';
import DirectorShell from '../components/director/DirectorShell';

// Append a cache-bust parameter to a media URL so the browser never serves stale content.
// Skips data-URLs (they are inline and never cached by the browser's HTTP cache).
const bustCache = (url) => {
  if (!url || url.startsWith('data:')) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}_t=${Date.now()}`;
};

const normalizeDirectorMediaUrl = (url) => {
  if (typeof url !== 'string' || !url) return url;
  if (
    url.startsWith('http://')
    || url.startsWith('https://')
    || url.startsWith('data:')
    || url.startsWith('blob:')
  ) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${directorApiClient.baseUrl}${url}`;
  }
  return url;
};

// Helper to flatten API scene structure
let _groupCounter = 0;
const normalizeScenes = (scenes) => {
  if (!scenes || !Array.isArray(scenes)) return [];

  let flatList = [];
  let pendingGroupHeader = null;
  let pendingGroupId = null;

  const processItems = (items, parentGroupId) => {
    items.forEach(item => {
      // 1. Check for nested group/beat (Explicit Group Object)
      if (item && typeof item === 'object' && item.scenes && Array.isArray(item.scenes)) {
        const groupNarration = item.narration || item.screenplay || item.description || item.header;
        const gid = item.group_id ?? item.id ?? `group-${++_groupCounter}`;
        const childrenStartIdx = flatList.length;
        processItems(item.scenes, gid);

        if (flatList.length > childrenStartIdx) {
          flatList[childrenStartIdx].groupHeader = groupNarration;
          flatList[childrenStartIdx].isGroupStart = true;
        }
        pendingGroupHeader = null;
        pendingGroupId = null;
      }
      // 2. Check explicitly for Header items (Flat List Headers)
      else if (item && (item.type === 'header' || item.type === 'section_header' || item.type === 'group' || (item.header && !item.image_url))) {
        pendingGroupHeader = item.header || item.title || item.narration || item.text || item.description;
        pendingGroupId = item.group_id ?? item.id ?? `group-${++_groupCounter}`;
      }
      // 3. Raw Array
      else if (Array.isArray(item)) {
        processItems(item, parentGroupId);
      }
      // 4. Scene Item
      else {
        let sceneData = item.data ? { ...item.data, ...item } : { ...item };
        if (item.data) Object.assign(sceneData, item.data);

        sceneData = {
          ...sceneData,
          type: sceneData.type || 'scene',
          // Screenplay sub-fields (V2) — fall back to legacy `screenplay` for older jobs
          subject_action: sceneData.subject_action || '',
          environment_motion: sceneData.environment_motion || '',
          atmospheric_detail: sceneData.atmospheric_detail || '',
          narration: sceneData.narration ?? null,
          action_timing: sceneData.action_timing ?? null,
          mood_direction: sceneData.mood_direction ?? null,
          // Legacy compat: keep screenplay for sidebar preview if sub-fields are empty
          screenplay: sceneData.screenplay || '',
          // Scripter V2 metadata
          scene_type: sceneData.scene_type ?? null,
          target_duration: sceneData.target_duration ?? null,
          video_provider: sceneData.video_provider ?? null,
          character_refs: sceneData.character_refs ?? null,
          // Slide-only
          offer: sceneData.offer ?? null,
          audio: sceneData.audio || '',
          edits: sceneData.edits || [],
          audio_constraints: sceneData.audio_constraints || '',
          content: sceneData.content || '',
          image_url: sceneData.image_url || sceneData.initial_frame_data_url || sceneData.data?.image_url,
          video_url: sceneData.video_url || sceneData.scene_video_url || sceneData.data?.video_url,
          slide_url: sceneData.slide_url || (sceneData.type === 'slide' ? sceneData.slide_image_data_url : null),
          photo_name_id: sceneData.photo_name_id
            || sceneData.photo_id
            || sceneData.source_photo_id
            || sceneData.source_photo_name_id
            || sceneData.selected_photo_id
            || null,
          photo_id: sceneData.photo_id || sceneData.source_photo_id || sceneData.selected_photo_id || null,
          group_id: sceneData.group_id ?? parentGroupId ?? pendingGroupId ?? null,
          group_narration: sceneData.group_narration ?? null
        };

        // Attach pending header from flat-list structure
        if (pendingGroupHeader) {
          sceneData.groupHeader = pendingGroupHeader;
          sceneData.isGroupStart = true;
          pendingGroupHeader = null;
        }

        // Fix relative URLs
        if (sceneData.image_url && sceneData.image_url.startsWith('/')) {
          sceneData.image_url = `${directorApiClient.baseUrl}${sceneData.image_url}`;
        }
        if (sceneData.video_url && sceneData.video_url.startsWith('/')) {
          sceneData.video_url = `${directorApiClient.baseUrl}${sceneData.video_url}`;
        }
        if (sceneData.slide_url && sceneData.slide_url.startsWith('/')) {
          sceneData.slide_url = `${directorApiClient.baseUrl}${sceneData.slide_url}`;
        }

        // Bust browser cache
        sceneData.image_url = bustCache(sceneData.image_url);
        sceneData.video_url = bustCache(sceneData.video_url);
        sceneData.slide_url = bustCache(sceneData.slide_url);

        flatList.push(sceneData);
      }
    });
  };

  processItems(scenes, null);

  // Re-assign strict linear indices
  return flatList.map((s, i) => ({ ...s, index: i }));
};

const normalizeBusinessData = (business, fallback = null) => {
  if (!business && !fallback) return null;

  const primary = business || {};
  const base = fallback || {};
  const resolvedPhotos = (primary.photos && primary.photos.length > 0)
    ? primary.photos
    : (base.photos || []);
  const resolvedReviews = primary.reviews || base.reviews || [];

  return {
    ...base,
    ...primary,
    photos: resolvedPhotos.map((photo) => {
      if (typeof photo === 'string') {
        return { data_url: photo, selected: true };
      }
      return {
        ...photo,
        data_url: normalizeDirectorMediaUrl(photo.data_url || photo.photo_url || ''),
        url: normalizeDirectorMediaUrl(photo.url || photo.photo_url || ''),
        selected: photo.selected !== undefined ? photo.selected : true,
      };
    }),
    reviews: resolvedReviews.map((review, idx) => {
      if (typeof review === 'string') {
        return {
          review_id: `review-${idx}`,
          text: review,
          selected: true,
        };
      }
      return {
        ...review,
        review_id: review.review_id || `review-${idx}`,
        selected: review.selected !== undefined ? review.selected : true,
      };
    }),
  };
};

const PHASES = ['CASTING', 'BRIEF', 'STUDIO', 'PREMIERE'];

const DirectorStudio = () => {
  const { jobId, orgSlug } = useParams();
  const navigate = useNavigate();
  const { currentOrg } = useOrganization();

  // --- STATE ---
  const [currentPhase, setCurrentPhase] = useState('CASTING');
  const [currentJobId, setCurrentJobId] = useState(jobId || null);
  const [jobStatus, setJobStatus] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(!!jobId);
  const [isMockEnabled] = useState(import.meta.env.VITE_ENABLE_MOCK_GENERATION === 'true');
  // Backend returns 404 when a job belongs to a different organization. We
  // gate the whole page on the first authoritative fetch (getJobStatus) so
  // the phase UI never renders against an unreachable job.
  const [notFoundInOrg, setNotFoundInOrg] = useState(false);

  // Business Data
  const [searchMethod, setSearchMethod] = useState('search');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [editingBusinessData, setEditingBusinessData] = useState(null);
  const [isBusinessDataLoading, setIsBusinessDataLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Script & Scenes
  const [scriptData, setScriptData] = useState(null);
  const [editingScriptData, setEditingScriptData] = useState(null);
  const [isScriptLoading, setIsScriptLoading] = useState(false);
  const [selectedSceneIndex, setSelectedSceneIndex] = useState(0);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [duration, setDuration] = useState(15);
  const [scriptMode, setScriptMode] = useState(DEFAULT_SCRIPT_MODE);
  const [voiceId, setVoiceId] = useState(null);
  const [showAspectRatioWarning, setShowAspectRatioWarning] = useState(false);

  // Logo Overlay Configuration
  const [logoOverlayEnabled, setLogoOverlayEnabled] = useState(false);
  const [logoOverlayPosition, setLogoOverlayPosition] = useState('bottom-right');
  const [logoOverlayOpacity, setLogoOverlayOpacity] = useState(0.8);
  const [videoConfigSaving, setVideoConfigSaving] = useState(false);
  const [photoUploadLoading, setPhotoUploadLoading] = useState(false);
  const [photoDeleteLoading, setPhotoDeleteLoading] = useState({});
  const [sceneSaveLoading, setSceneSaveLoading] = useState({});
  const [characters, setCharacters] = useState([]);

  // Campaign Brief
  const [campaignBrief, setCampaignBrief] = useState({
    purpose: 'awareness',
    seasonal_theme: null,
    tone: 'auto',
    channel: 'auto',
    offers: [],
    notes: ''
  });
  const [campaignOptions, setCampaignOptions] = useState(null);

  // Per-scene generation loading states
  const [frameGenerationLoading, setFrameGenerationLoading] = useState({});
  const [slideGenerationLoading, setSlideGenerationLoading] = useState({});
  const [videoGenerationLoading, setVideoGenerationLoading] = useState({});

  // Video Generation
  const [fullVideoLoading, setFullVideoLoading] = useState(false);

  // Modal state
  const [showChangeBusinessModal, setShowChangeBusinessModal] = useState(false);

  // Mobile sidebar state (for Story Stream in Studio phase)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Advertiser selection for org context
  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState(null);
  const selectedPlaceId = selectedBusiness?.place_id || null;

  // --- API DATA LOADING ---
  const loadInitialData = async (id) => {
    setIsInitialLoading(true);
    setNotFoundInOrg(false);
    setScriptMode(DEFAULT_SCRIPT_MODE);
    try {
      const [statusResult, businessResult, scriptResult, videoConfigResult, campaignBriefResult, charactersResult] = await Promise.allSettled([
        directorApiClient.getJobStatus(id),
        directorApiClient.getBusinessData(id),
        directorApiClient.getScript(id),
        directorApiClient.getVideoConfig(id),
        directorApiClient.getCampaignBrief(id),
        directorApiClient.getCharacters(id)
      ]);

      // Cross-org access: backend 404s on every job-scoped route. Use
      // getJobStatus as the authoritative gate — if it 404s the job is
      // unreachable from the current org and the phases must not render.
      if (statusResult.status === 'rejected' && statusResult.reason?.status === 404) {
        setNotFoundInOrg(true);
        return;
      }

      if (statusResult.status === 'fulfilled') {
        setJobStatus(statusResult.value);
      }

      let hasBusinessData = false;
      if (businessResult.status === 'fulfilled' && businessResult.value) {
        const bResult = businessResult.value;
        let bData = bResult.result ? (bResult.result.business || bResult.result.business_data || bResult.result) : (bResult.business || bResult.business_data || bResult);

        if (bData && Object.keys(bData).length > 0) {
          const merged = normalizeBusinessData(bData);
          setBusinessData(merged);
          setEditingBusinessData(merged);
          hasBusinessData = true;
        }
      }

      let hasScript = false;
      if (scriptResult.status === 'fulfilled' && scriptResult.value) {
        const sResult = scriptResult.value;
        const sData = sResult.result || sResult;

        if (sData && Array.isArray(sData.scenes)) {
          const normalizedScenes = normalizeScenes(sData.scenes);
          if (normalizedScenes.length > 0) {
            const normalizedScriptData = {
              ...sData,
              scenes: normalizedScenes,
            };
            setScriptData(normalizedScriptData);
            setEditingScriptData(normalizedScriptData);
            setSelectedSceneIndex(0); // Reset to first scene when loading script data
            hasScript = true;
          } else {
            setScriptData(null);
            setEditingScriptData(null);
          }
        }
      }

      // Load video config (aspect ratio and logo overlay settings)
      if (videoConfigResult.status === 'fulfilled' && videoConfigResult.value) {
        const config = videoConfigResult.value.result || videoConfigResult.value;
        if (config.aspect_ratio) {
          setAspectRatio(config.aspect_ratio);
        }
        const nextDuration = Number(config.length ?? config.duration);
        if (Number.isFinite(nextDuration) && nextDuration > 0) {
          setDuration(nextDuration);
        }
        setScriptMode(normalizeScriptMode(config.script_mode));
        setVoiceId(config.voice_id ?? null);
        // Load logo overlay settings
        if (config.logo_overlay_enabled !== undefined) {
          setLogoOverlayEnabled(config.logo_overlay_enabled);
        }
        if (config.logo_overlay_position) {
          setLogoOverlayPosition(config.logo_overlay_position);
        }
        if (config.logo_overlay_opacity !== undefined) {
          setLogoOverlayOpacity(config.logo_overlay_opacity);
        }
      }

      // Load campaign brief
      if (campaignBriefResult.status === 'fulfilled' && campaignBriefResult.value) {
        const briefData = campaignBriefResult.value.brief || campaignBriefResult.value;
        if (briefData) {
          setCampaignBrief(prev => ({
            ...prev,
            ...briefData
          }));
        }
      }

      if (charactersResult.status === 'fulfilled' && charactersResult.value) {
        const nextCharacters = Array.isArray(charactersResult.value?.characters)
          ? charactersResult.value.characters
          : Array.isArray(charactersResult.value)
            ? charactersResult.value
            : [];
        setCharacters(nextCharacters);
      } else {
        setCharacters([]);
      }

      // Auto-advance phase based on loaded data
      const status = statusResult.status === 'fulfilled' ? statusResult.value : null;
      const normalizedStatus = String(status?.state || status?.status || '').toLowerCase();
      const isCompleted = ['completed', 'done', 'ready', 'success', 'succeeded'].includes(normalizedStatus)
        || Boolean(status?.assets?.video_url)
        || Boolean(status?.completedAt || status?.completed_at);
      if (isCompleted && hasScript) {
        setCurrentPhase('PREMIERE');
      } else if (hasScript) {
        setCurrentPhase('STUDIO');
      } else if (hasBusinessData) {
        setCurrentPhase('BRIEF');
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const fetchJobStatus = async (id) => {
    try {
      const status = await directorApiClient.getJobStatus(id);
      setJobStatus(status);
    } catch (err) {
      console.error("Error fetching job status:", err);
    }
  };

  useEffect(() => {
    if (jobId) {
      setCurrentJobId(jobId);
      loadInitialData(jobId);
    }
  }, [jobId]);

  // Fetch campaign options on mount
  useEffect(() => {
    const fetchCampaignOptions = async () => {
      try {
        const options = await directorApiClient.getCampaignOptions();
        setCampaignOptions(options);
      } catch (error) {
        console.error('Failed to load campaign options:', error);
      }
    };
    fetchCampaignOptions();
  }, []);

  // --- HANDLERS ---
  const handleGooglePlaceSelect = (place) => {
    if (!place || !place.placeId) {
      toast.error("Could not select place. Missing Place ID.");
      return;
    }

    const selectedData = {
      name: place.name,
      address: place.address,
      place_id: place.placeId,
      phoneNumber: place.phoneNumber,
      website: place.website,
      rating: place.rating,
      photos: (place.photos || []).map(p => {
        if (typeof p.getUrl === 'function') return p.getUrl();
        if (p.url) return p.url;
        if (typeof p === 'string') return p;
        return null;
      }).filter(Boolean)
    };

    setSelectedBusiness(selectedData);
    setApiError(null);
  };

  const handleStartProject = async (source) => {
    if (!source?.place_id && !source?.website_url) {
      setApiError('Please select a business or enter a website URL.');
      return;
    }

    setApiError(null);
    setIsBusinessDataLoading(true);

    const selectedData = source.place_id ? selectedBusiness : null;
    try {
      const response = await directorApiClient.createProject(source, {
        advertiser_id: selectedAdvertiserId || undefined
      });
      const newJobId = response.job_id || response.jobId || (response.result && response.result.job_id);
      const backendBusinessData = response.business_data
        || (response.result && (response.result.business_data || response.result.business));
      const mergedData = normalizeBusinessData(backendBusinessData, selectedData);

      if (mergedData) {
        setBusinessData(mergedData);
        setEditingBusinessData(mergedData);
      }

      setCharacters([]);

      if (!newJobId) {
        throw new Error('Project initialization did not return a job ID.');
      }

      setCurrentJobId(newJobId);
      const basePath = currentOrg ? `/org/${currentOrg.slug}` : (orgSlug ? `/org/${orgSlug}` : '');
      navigate(`${basePath}/director/${newJobId}`, { replace: true });
    } catch (err) {
      console.error('Error creating project:', err);
      setApiError(err.message || 'Failed to initialize project. Please try again.');
    } finally {
      setIsBusinessDataLoading(false);
    }
  };

  const handleSaveBrief = async () => {
    if (saveLoading) return;
    setSaveLoading(true);
    try {
      const selectedPhotoIds = (editingBusinessData.photos || [])
        .filter(photo => photo.selected)
        .map(photo => photo.photo_id)
        .filter(Boolean);

      const selectedReviewIds = (editingBusinessData.reviews || [])
        .filter(review => review.selected)
        .map(review => review.review_id)
        .filter(Boolean);

      const updates = {
        name: editingBusinessData.name,
        address: editingBusinessData.address,
        description: editingBusinessData.description,
        selected_photo_ids: selectedPhotoIds,
        selected_review_ids: selectedReviewIds,
        additional_info: {
          cuisine: Array.isArray(editingBusinessData.additional_info?.cuisine) ? editingBusinessData.additional_info.cuisine.join(',') : (editingBusinessData.additional_info?.cuisine || ''),
          price_range: editingBusinessData.additional_info?.price_range || '',
          social_media: (typeof editingBusinessData.additional_info?.social_media === 'object') ? JSON.stringify(editingBusinessData.additional_info.social_media) : (editingBusinessData.additional_info?.social_media || ''),
          current_opening_hours: editingBusinessData.additional_info?.current_opening_hours || '',
          website_uri: editingBusinessData.website || editingBusinessData.additional_info?.website_uri || ''
        }
      };

      // Save business data
      await directorApiClient.updateBusinessData(currentJobId, updates);

      // Save video configuration
      await directorApiClient.updateVideoConfig(currentJobId, {
        aspect_ratio: aspectRatio,
        length: duration,
        script_mode: scriptMode,
        logo_overlay_enabled: logoOverlayEnabled,
        logo_overlay_position: logoOverlayPosition,
        logo_overlay_opacity: logoOverlayOpacity
      });

      // Save campaign brief
      await directorApiClient.updateCampaignBrief(currentJobId, campaignBrief);

      setBusinessData({ ...editingBusinessData });
      toast.success("Brief approved and saved!");
      setCurrentPhase('STUDIO');
    } catch (err) {
      console.error("Failed to save brief:", err);
      toast.error("Failed to save brief changes.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePhotoToggle = (photoId, index) => {
    setEditingBusinessData(prev => ({
      ...prev,
      photos: prev.photos.map((photo, i) => {
        if (photoId && photo.photo_id === photoId) return { ...photo, selected: !photo.selected };
        if (index !== undefined && i === index) return { ...photo, selected: !photo.selected };
        return photo;
      })
    }));
  };

  const handleReviewToggle = (reviewId) => {
    setEditingBusinessData(prev => ({
      ...prev,
      reviews: (prev.reviews || []).map(review => {
        if (review.review_id === reviewId) return { ...review, selected: !review.selected };
        return review;
      })
    }));
  };

  const handlePhotoUpload = async (file) => {
    if (!currentJobId || photoUploadLoading) return;
    setPhotoUploadLoading(true);
    try {
      const dataUrl = await directorApiClient.fileToDataUrl(file);
      const response = await directorApiClient.uploadPhoto(currentJobId, dataUrl, file.name);
      const newPhoto = {
        photo_id: response.photo_id,
        photo_name_id: response.photo_name_id,
        data_url: dataUrl,
        caption: file.name,
        selected: true
      };
      setEditingBusinessData(prev => ({ ...prev, photos: [...(prev.photos || []), newPhoto] }));
      toast.success("Photo uploaded");
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setPhotoUploadLoading(false);
    }
  };

  const handlePhotoDelete = async (photoId) => {
    if (!currentJobId || photoDeleteLoading[photoId]) return;
    setPhotoDeleteLoading(prev => ({ ...prev, [photoId]: true }));
    try {
      await directorApiClient.deletePhoto(currentJobId, photoId);
      setEditingBusinessData(prev => ({
        ...prev,
        photos: prev.photos.filter(photo => photo.photo_id !== photoId)
      }));
      toast.success("Photo deleted");
    } catch (error) {
      toast.error("Failed to delete photo");
    } finally {
      setPhotoDeleteLoading(prev => {
        const next = { ...prev };
        delete next[photoId];
        return next;
      });
    }
  };

  const handleLogoUpload = async (file) => {
    if (!currentJobId) return;

    try {
      const dataUrl = await directorApiClient.fileToDataUrl(file);
      const response = await directorApiClient.uploadLogo(currentJobId, dataUrl);
      const nextLogoUrl = response?.logo_data_url || dataUrl;

      setEditingBusinessData(prev => ({
        ...prev,
        logo_data_url: nextLogoUrl,
        brand_identity: {
          ...(prev?.brand_identity || {}),
          logo_data_url: nextLogoUrl,
          logo_url: nextLogoUrl,
        },
      }));
      toast.success('Logo uploaded');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error('Failed to upload logo');
    }
  };

  const handleLogoDelete = async () => {
    if (!currentJobId) return;

    try {
      await directorApiClient.deleteLogo(currentJobId);
      setEditingBusinessData(prev => ({
        ...prev,
        logo_data_url: null,
        brand_identity: prev?.brand_identity
          ? {
              ...prev.brand_identity,
              logo_data_url: null,
              logo_url: null,
            }
          : prev?.brand_identity,
      }));
      toast.success('Logo removed');
    } catch (error) {
      console.error('Failed to delete logo:', error);
      toast.error('Failed to remove logo');
    }
  };

  const handleGenerateScript = async () => {
    setIsScriptLoading(true);
    try {
      // First, update the video configuration with aspect ratio
      await directorApiClient.updateVideoConfig(currentJobId, {
        aspect_ratio: aspectRatio,
        length: duration,
        script_mode: scriptMode
      });

      // Generate the script with configuration
      const result = await directorApiClient.generateScript(currentJobId, {
        campaign: {
          objective: 'brand_awareness',
          distribution_channels: aspectRatio === '9:16' ? 'social' : 'ctv',
          notes: `Optimized for ${aspectRatio} aspect ratio`
        },
        metadata: {
          requested_by: 'cornercast-director-studio'
        },
        additional_context: `Generate content optimized for ${aspectRatio === '9:16' ? 'mobile social media' : 'connected TV and desktop'} viewing.`
      });

      const sData = result.result || result;
      if (sData.scenes) sData.scenes = normalizeScenes(sData.scenes);

      setScriptData(sData);
      setEditingScriptData(sData);
      setSelectedSceneIndex(0); // Reset to first scene after script generation
      setCurrentPhase('STUDIO');
      toast.success("Script generated successfully!");
    } catch (err) {
      console.error("Generate Script Error:", err);
      setApiError("Failed to generate script. Please try again.");
    } finally {
      setIsScriptLoading(false);
    }
  };

  const handleCharacterUpload = async (dataUrls, metadata = {}) => {
    if (!currentJobId) return;

    try {
      const response = await directorApiClient.uploadCharacter(currentJobId, {
        data_urls: dataUrls,
        ...metadata,
      });
      setCharacters(prev => [...prev, response]);
      toast.success('Character added');
    } catch (error) {
      console.error('Failed to upload character:', error);
      toast.error('Failed to upload character');
    }
  };

  const handleCharacterUpdate = async (characterId, updates) => {
    if (!currentJobId) return;

    try {
      const response = await directorApiClient.updateCharacter(currentJobId, characterId, updates);
      setCharacters(prev => prev.map((character) => (
        character.character_id === characterId ? { ...character, ...response } : character
      )));
      toast.success('Character updated');
    } catch (error) {
      console.error('Failed to update character:', error);
      toast.error('Failed to update character');
    }
  };

  const handleCharacterDelete = async (characterId) => {
    if (!currentJobId) return;

    try {
      await directorApiClient.deleteCharacter(currentJobId, characterId);
      setCharacters(prev => prev.filter((character) => character.character_id !== characterId));
      toast.success('Character removed');
    } catch (error) {
      console.error('Failed to delete character:', error);
      toast.error('Failed to delete character');
    }
  };

  // Local handler for Studio Phase editing (typing in fields)
  // Accepts partial updates (just the changed fields) and merges with current state
  const handleLocalSceneUpdate = (index, updates) => {
    setEditingScriptData(prev => ({
      ...prev,
      scenes: prev.scenes.map((scene, i) =>
        i === index ? { ...scene, ...updates } : scene
      )
    }));
  };

  // API Save Handler
  const updateScene = async (index, updatedScene) => {
    // Ensure local state is sync (it should be via handleLocalSceneUpdate, but good to be sure)
    handleLocalSceneUpdate(index, updatedScene);

    if (!currentJobId || sceneSaveLoading[index]) return false;
    setSceneSaveLoading(prev => ({ ...prev, [index]: true }));
    try {
      const apiUpdates = {
        duration: updatedScene.duration,
        // Screenplay sub-fields (V2)
        subject_action: updatedScene.subject_action,
        environment_motion: updatedScene.environment_motion,
        atmospheric_detail: updatedScene.atmospheric_detail,
        narration: updatedScene.narration,
        action_timing: updatedScene.action_timing,
        mood_direction: updatedScene.mood_direction,
        // Scripter V2 metadata
        scene_type: updatedScene.scene_type,
        video_provider: updatedScene.video_provider,
        character_refs: updatedScene.character_refs,
        // Core fields
        audio: updatedScene.audio,
        photo_name_id: updatedScene.photo_name_id,
        edits: updatedScene.edits || [],
        content: updatedScene.content,
        audio_constraints: updatedScene.audio_constraints,
        shot: updatedScene.shot || {},
        offer: updatedScene.offer,
      };

      await directorApiClient.updateScene(currentJobId, index, apiUpdates);
      toast.success("Scene saved");
      return true;
    } catch (e) {
      console.error("Failed to save scene:", e);
      toast.error("Failed to save scene change");
      return false;
    } finally {
      setSceneSaveLoading(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const generateInitialFrame = async (index) => {
    if (!currentJobId) {
      setApiError('No project ID available.');
      return;
    }

    setFrameGenerationLoading(prev => ({ ...prev, [index]: true }));
    try {
      toast.loading('Generating frame...', { id: 'genFrame' });
      // The endpoint returns 202 Accepted with { operation_id, status_url, asset_url };
      // the actual frame is produced by a background task.
      const accepted = await directorApiClient.generateInitialFrame(currentJobId, index);
      const operationId = accepted?.operation_id;
      if (!operationId) {
        throw new Error('Server did not return an operation_id for frame generation');
      }

      // Poll the operation until it reaches a terminal state.
      const status = await directorApiClient.waitForOperation(operationId);

      if (String(status?.state || '').toLowerCase() === 'failed') {
        const detail = status?.error_detail || status?.error_code || 'unknown error';
        throw new Error(`Frame generation failed: ${detail}`);
      }

      // Resolve the produced asset URL (server returns a relative path).
      const rawAssetUrl = status?.result_url || accepted?.asset_url;
      const frameDataUrl = normalizeDirectorMediaUrl(rawAssetUrl);

      // Use functional update to preserve any edits made while waiting for the operation.
      setEditingScriptData(prev => ({
        ...prev,
        scenes: prev.scenes.map((scene, i) =>
          i === index
            ? { ...scene, image_url: bustCache(frameDataUrl), initial_frame: bustCache(frameDataUrl) }
            : scene
        )
      }));
      toast.success('Frame generated!', { id: 'genFrame' });
    } catch (error) {
      console.error('Failed to generate initial frame:', error);
      toast.error('Failed to generate frame', { id: 'genFrame' });
      setApiError(`Failed to generate frame for scene ${index + 1}: ${error.message}`);
    } finally {
      setFrameGenerationLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  const generateFullVideo = async () => {
    if (!currentJobId) return;
    setFullVideoLoading(true);

    // Clear existing video state so Premiere shows loading state
    setJobStatus(prev => prev ? { ...prev, state: 'processing', assets: { ...prev.assets, video_url: null } } : { state: 'processing' });
    setEditingScriptData(prev => prev ? { ...prev, final_video_url: null } : prev);
    setScriptData(prev => prev ? { ...prev, final_video_url: null } : prev);

    try {
      await directorApiClient.generateFullVideo(currentJobId);
      toast.success("Video generation started!");

      // Poll for job completion
      const pollInterval = setInterval(async () => {
        try {
          const status = await directorApiClient.getJobStatus(currentJobId);
          setJobStatus(status);

          if (status.state === 'completed' || status.state === 'failed') {
            clearInterval(pollInterval);
            setFullVideoLoading(false);

            if (status.state === 'failed') {
              setApiError('Video generation failed. Please try again.');
            } else {
              toast.success("Video generation complete!");
            }
          }
        } catch (pollError) {
          console.error('Failed to poll job status:', pollError);
        }
      }, 2000);

      // Cleanup interval after 5 minutes max
      setTimeout(() => {
        clearInterval(pollInterval);
        setFullVideoLoading(false);
      }, 300000);
    } catch (error) {
      setApiError("Failed to start video generation.");
      setFullVideoLoading(false);
    }
  };

  // Refresh business selection (clear and start over)
  const refreshBusinessSelection = () => {
    setSearchMethod('search');
    setSelectedBusiness(null);
    setBusinessData(null);
    setEditingBusinessData(null);
    setScriptData(null);
    setEditingScriptData(null);
    setCharacters([]);
    setCurrentJobId(null);
    setDuration(15);
    setScriptMode(DEFAULT_SCRIPT_MODE);
    setApiError(null);
    setCurrentPhase('CASTING');
    const basePath = currentOrg ? `/org/${currentOrg.slug}` : (orgSlug ? `/org/${orgSlug}` : '');
    navigate(`${basePath}/director`, { replace: true });
  };

  // Refresh enrichment data (re-scrape business info, branding, etc.)
  const refreshBusinessData = async () => {
    if (!currentJobId) {
      setApiError('No project ID available to refresh.');
      return;
    }
    setIsBusinessDataLoading(true);
    try {
      // Call enrichment refresh endpoint to re-scrape data
      await directorApiClient.refreshEnrichment(currentJobId);
      // Then reload all data to get the refreshed results
      await loadInitialData(currentJobId);
      toast.success("Data refreshed successfully");
    } catch (error) {
      console.error("Failed to refresh enrichment:", error);
      setApiError("Failed to refresh data: " + (error.message || "Unknown error"));
    } finally {
      setIsBusinessDataLoading(false);
    }
  };

  // Update video configuration (aspect ratio)
  const updateVideoConfig = async (config) => {
    if (!currentJobId || isMockEnabled || videoConfigSaving) return false;
    setVideoConfigSaving(true);
    try {
      await directorApiClient.updateVideoConfig(currentJobId, config);
      console.debug('Updated video config:', config);
      return true;
    } catch (error) {
      console.error('Failed to update video config:', error);
      setApiError(`Failed to update video configuration: ${error.message}`);
      return false;
    } finally {
      setVideoConfigSaving(false);
    }
  };

  // Handle aspect ratio change with warning
  const handleAspectRatioChange = async (newRatio) => {
    if (videoConfigSaving) return;
    const previousRatio = aspectRatio;
    setAspectRatio(newRatio);

    // Only show warning if there are actual scenes to review
    const hasScenes = scriptData?.scenes?.length > 0;
    if (hasScenes && previousRatio !== newRatio) {
      setShowAspectRatioWarning(true);
      setTimeout(() => setShowAspectRatioWarning(false), 8000);
    }

    // Update video config in API
    await updateVideoConfig({ aspect_ratio: newRatio });
  };

  const handleLogoOverlayOpacityPreview = (opacity) => {
    setLogoOverlayOpacity(opacity);
  };

  const handleScriptModeChange = async (newMode) => {
    if (videoConfigSaving) return;
    setScriptMode(newMode);
    await updateVideoConfig({ script_mode: newMode });
  };

  const handleVoiceChange = async (newVoiceId) => {
    if (videoConfigSaving) return;
    setVoiceId(newVoiceId);
    await updateVideoConfig({ voice_id: newVoiceId });
  };

  const handleDurationChange = async (newDuration) => {
    if (videoConfigSaving) return;
    setDuration(newDuration);
    await updateVideoConfig({ length: newDuration });
  };

  // Handle logo overlay configuration changes
  // Accepts an object with one or more of: enabled, position, opacity
  const handleLogoOverlayChange = async (updates) => {
    if (videoConfigSaving) return;
    if (updates.enabled !== undefined) {
      setLogoOverlayEnabled(updates.enabled);
    }
    if (updates.position !== undefined) {
      setLogoOverlayPosition(updates.position);
    }
    if (updates.opacity !== undefined) {
      setLogoOverlayOpacity(updates.opacity);
    }

    // Build API update payload
    const configUpdate = {};
    if (updates.enabled !== undefined) configUpdate.logo_overlay_enabled = updates.enabled;
    if (updates.position !== undefined) configUpdate.logo_overlay_position = updates.position;
    if (updates.opacity !== undefined) configUpdate.logo_overlay_opacity = updates.opacity;

    if (Object.keys(configUpdate).length > 0) {
      await updateVideoConfig(configUpdate);
    }
  };

  // Generate slide for a SlideScene
  const generateSlide = async (sceneIndex) => {
    if (!currentJobId) {
      setApiError('No project ID available.');
      return;
    }

    setSlideGenerationLoading(prev => ({ ...prev, [sceneIndex]: true }));
    try {
      toast.loading('Generating slide...', { id: 'genSlide' });
      // 202 Accepted with { operation_id, status_url, asset_url }; render runs in background.
      const accepted = await directorApiClient.generateSlide(currentJobId, sceneIndex);
      const operationId = accepted?.operation_id;
      if (!operationId) {
        throw new Error('Server did not return an operation_id for slide generation');
      }

      const status = await directorApiClient.waitForOperation(operationId);

      if (String(status?.state || '').toLowerCase() === 'failed') {
        const detail = status?.error_detail || status?.error_code || 'unknown error';
        throw new Error(`Slide generation failed: ${detail}`);
      }

      const rawAssetUrl = status?.result_url || accepted?.asset_url;
      const slideDataUrl = normalizeDirectorMediaUrl(rawAssetUrl);

      // Update the scene data to include the new slide
      setEditingScriptData(prev => ({
        ...prev,
        scenes: prev.scenes.map((scene, idx) =>
          idx === sceneIndex ? { ...scene, slide_url: bustCache(slideDataUrl) } : scene
        )
      }));

      toast.success('Slide generated!', { id: 'genSlide' });
    } catch (error) {
      console.error('Failed to generate slide:', error);
      toast.error('Failed to generate slide', { id: 'genSlide' });
      setApiError(`Failed to generate slide for scene ${sceneIndex + 1}: ${error.message}`);
    } finally {
      setSlideGenerationLoading(prev => ({ ...prev, [sceneIndex]: false }));
    }
  };

  // Generate video for a specific scene
  const generateSceneVideo = async (sceneIndex) => {
    if (!currentJobId) {
      setApiError('No project ID available.');
      return;
    }

    setVideoGenerationLoading(prev => ({ ...prev, [sceneIndex]: true }));
    try {
      toast.loading('Generating scene video...', { id: 'genVideo' });
      // 202 Accepted with { operation_id, status_url, asset_url }; render runs in background.
      const accepted = await directorApiClient.generateSceneVideo(currentJobId, sceneIndex);
      const operationId = accepted?.operation_id;
      if (!operationId) {
        throw new Error('Server did not return an operation_id for scene video generation');
      }

      // Scene videos can take longer than the default 5 minute cap; bump to 15.
      const status = await directorApiClient.waitForOperation(operationId, {
        timeoutMs: 900000,
      });

      if (String(status?.state || '').toLowerCase() === 'failed') {
        const detail = status?.error_detail || status?.error_code || 'unknown error';
        throw new Error(`Scene video generation failed: ${detail}`);
      }

      // Resolve the produced asset URL (server returns a relative path).
      const rawVideoUrl = status?.result_url || accepted?.asset_url;
      const videoUrl = normalizeDirectorMediaUrl(rawVideoUrl);

      // Update the scene data to include the new video
      if (videoUrl) {
        setEditingScriptData(prev => ({
          ...prev,
          scenes: prev.scenes.map((scene, idx) =>
            idx === sceneIndex ? { ...scene, video_url: bustCache(videoUrl) } : scene
          )
        }));
      }

      toast.success('Scene video generated!', { id: 'genVideo' });
    } catch (error) {
      console.error('Failed to generate scene video:', error);
      toast.error('Failed to generate scene video', { id: 'genVideo' });
      setApiError(`Failed to generate video for scene ${sceneIndex + 1}: ${error.message}`);
    } finally {
      setVideoGenerationLoading(prev => ({ ...prev, [sceneIndex]: false }));
    }
  };

  // Save all scenes at once
  const saveAllScenes = async () => {
    if (!currentJobId || !editingScriptData?.scenes || saveLoading || Object.values(sceneSaveLoading).some(Boolean)) return;

    setSaveLoading(true);
    try {
      for (let i = 0; i < editingScriptData.scenes.length; i++) {
        await updateScene(i, editingScriptData.scenes[i]);
      }
      toast.success("All scenes saved!");
    } catch (error) {
      setApiError("Failed to save all scenes");
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePhaseChange = (targetPhase) => {
    if (isInitialLoading) {
      toast.info("Loading project data, please wait...");
      return;
    }
    if (targetPhase === 'BRIEF' && !currentJobId) {
      toast.warning("Please select a business to start a project.");
      return;
    }
    if (targetPhase === 'STUDIO' && !scriptData) {
      toast.warning("Please generate a script (approve brief) first.");
      return;
    }
    if (targetPhase === 'PREMIERE' && !scriptData) {
      toast.warning("Work is not ready for premiere yet.");
      return;
    }
    setCurrentPhase(targetPhase);
  };

  const phaseOptions = [
    { value: 'CASTING', title: 'Casting', mobileLabel: '1. Casting' },
    { value: 'BRIEF', title: 'Brief', mobileLabel: '2. Brief', disabled: !currentJobId },
    { value: 'STUDIO', title: 'Studio', mobileLabel: '3. Studio', disabled: !scriptData },
    { value: 'PREMIERE', title: 'Premiere', mobileLabel: '4. Premiere', disabled: !scriptData },
  ];

  return (
    <DirectorShell
      currentPhase={currentPhase}
      phaseOptions={phaseOptions}
      onPhaseChange={handlePhaseChange}
      currentJobId={currentJobId}
      isMockEnabled={isMockEnabled}
      onChangeBusiness={currentJobId && !notFoundInOrg ? () => setShowChangeBusinessModal(true) : null}
      showSidebarToggle={currentPhase === 'STUDIO'}
      onSidebarToggle={() => setIsSidebarOpen((open) => !open)}
    >
      {/* Change Business Confirmation Dialog */}
      <Dialog open={showChangeBusinessModal} onOpenChange={setShowChangeBusinessModal}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Change Business?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will clear all current work and start a new project with a different business. Any unsaved changes will be lost.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangeBusinessModal(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowChangeBusinessModal(false);
                refreshBusinessSelection();
              }}
            >
              Change Business
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {apiError && (
        <div className="mx-auto w-full max-w-[1600px] px-4 pt-4 sm:px-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription className="flex items-center justify-between gap-3">
              <span>{apiError}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => setApiError(null)} aria-label="Dismiss error">
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col">
        {isInitialLoading ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-muted-foreground">Loading project data…</p>
          </div>
        ) : notFoundInOrg ? (
          <div className="flex flex-1 flex-col items-center justify-center px-4">
            <EmptyState
              icon={FolderSearch}
              title={currentOrg?.name
                ? `This project isn’t in ${currentOrg.name}`
                : 'This project isn’t in the current organization'}
              description="It may belong to a different organization, or it may have been removed. Switch organizations or browse your assets to find it."
            >
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  onClick={() => navigate(currentOrg ? `/org/${currentOrg.slug}/assets` : '/organizations')}
                >
                  Go to Assets
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/organizations')}
                >
                  Switch organization
                </Button>
              </div>
              {currentJobId && (
                <p className="mt-4 text-[10px] uppercase tracking-widest text-muted-foreground/40">
                  Job ID: {currentJobId}
                </p>
              )}
            </EmptyState>
          </div>
        ) : (
          <>
            {currentPhase === 'CASTING' && (
              <CastingPhase
                searchMethod={searchMethod}
                setSearchMethod={setSearchMethod}
                selectedBusiness={selectedBusiness}
                selectedPlaceId={selectedPlaceId}
                onPlaceSelect={handleGooglePlaceSelect}
                onStartProject={handleStartProject}
                currentOrg={currentOrg}
                selectedAdvertiserId={selectedAdvertiserId}
                setSelectedAdvertiserId={setSelectedAdvertiserId}
                apiError={apiError}
                loading={isBusinessDataLoading}
              />
            )}
            {currentPhase === 'BRIEF' && (
              <BriefPhase
                data={editingBusinessData}
                onSave={handleSaveBrief}
                loading={saveLoading}
                onPhotoToggle={handlePhotoToggle}
                onPhotoUpload={handlePhotoUpload}
                onPhotoDelete={handlePhotoDelete}
                onReviewToggle={handleReviewToggle}
                characters={characters}
                onCharacterUpload={handleCharacterUpload}
                onCharacterUpdate={handleCharacterUpdate}
                onCharacterDelete={handleCharacterDelete}
                onLogoUpload={handleLogoUpload}
                onLogoDelete={handleLogoDelete}
                onRefresh={refreshBusinessData}
                isRefreshing={isBusinessDataLoading}
                videoConfigLoading={videoConfigSaving || saveLoading}
                // Video config props
                aspectRatio={aspectRatio}
                onAspectRatioChange={handleAspectRatioChange}
                duration={duration}
                onDurationChange={handleDurationChange}
                scriptMode={scriptMode}
                onScriptModeChange={handleScriptModeChange}
                voiceId={voiceId}
                onVoiceChange={handleVoiceChange}
                logoOverlayEnabled={logoOverlayEnabled}
                logoOverlayPosition={logoOverlayPosition}
                logoOverlayOpacity={logoOverlayOpacity}
                onLogoOverlayChange={handleLogoOverlayChange}
                onLogoOverlayOpacityPreview={handleLogoOverlayOpacityPreview}
                photoUploadLoading={photoUploadLoading}
                photoDeleteLoading={photoDeleteLoading}
                // Campaign brief props
                campaignBrief={campaignBrief}
                onCampaignBriefChange={setCampaignBrief}
                campaignOptions={campaignOptions}
                // Advertiser selection (org context)
                selectedAdvertiserId={selectedAdvertiserId}
                onAdvertiserChange={setSelectedAdvertiserId}
                showAdvertiserSelector={!!currentOrg}
              />
            )}
            {currentPhase === 'STUDIO' && (
              <StudioPhase
                scriptData={editingScriptData}
                selectedSceneIndex={selectedSceneIndex}
                setSelectedSceneIndex={setSelectedSceneIndex}
                onLocalUpdate={handleLocalSceneUpdate}
                onSaveScene={updateScene}
                onGenerateFrame={generateInitialFrame}
                onGenerateSlide={generateSlide}
                onGenerateSceneVideo={generateSceneVideo}
                onGenerateScript={handleGenerateScript}
                onSaveAllScenes={saveAllScenes}
                loading={isScriptLoading}
                saveLoading={saveLoading}
                sceneSaveLoading={!!sceneSaveLoading[selectedSceneIndex]}
                isAnySceneSaveLoading={Object.values(sceneSaveLoading).some(Boolean)}
                frameGenerationLoading={frameGenerationLoading}
                slideGenerationLoading={slideGenerationLoading}
                videoGenerationLoading={videoGenerationLoading}
                aspectRatio={aspectRatio}
                onAspectRatioChange={handleAspectRatioChange}
                showAspectRatioWarning={showAspectRatioWarning}
                onDismissWarning={() => setShowAspectRatioWarning(false)}
                businessData={editingBusinessData}
                isSidebarOpen={isSidebarOpen}
                onCloseSidebar={() => setIsSidebarOpen(false)}
                onOpenSidebar={() => setIsSidebarOpen(true)}
                logoOverlayEnabled={logoOverlayEnabled}
                logoOverlayPosition={logoOverlayPosition}
                logoOverlayOpacity={logoOverlayOpacity}
                onLogoOverlayChange={handleLogoOverlayChange}
              />
            )}
            {currentPhase === 'PREMIERE' && (
              <PremierePhase
                scriptData={editingScriptData}
                jobStatus={jobStatus}
                onGenerateVideo={generateFullVideo}
                loading={fullVideoLoading}
                aspectRatio={aspectRatio}
                businessName={editingBusinessData?.name || businessData?.name || ''}
              />
            )}
          </>
        )}
      </div>
    </DirectorShell>
  );
};

export default DirectorStudio;
